// Template configuration for 10 Pro types with full branding, services, and content

export type ProTemplateId = 
  | 'general-contractor'
  | 'custom-home-builder'
  | 'roofer'
  | 'plumber'
  | 'electrician'
  | 'hvac'
  | 'remodeler'
  | 'painter'
  | 'landscaper'
  | 'flooring';

export interface TeamMember {
  name: string;
  role: string;
  bio: string;
  image?: string;
}

export interface TrustBadge {
  icon: string;
  label: string;
}

export interface ServiceItem {
  title: string;
  description: string;
  icon: string;
  slug: string;
  features?: string[];
}

export interface Testimonial {
  name: string;
  rating: number;
  text: string;
  location: string;
  service: string;
  avatar?: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: 'maintenance' | 'cost-guides' | 'how-to' | 'hiring-tips';
  date: string;
  readTime: string;
  image?: string;
}

export interface ServiceArea {
  name: string;
  description: string;
}

export interface ProTemplate {
  id: ProTemplateId;
  brand: {
    name: string;
    tagline: string;
    logo?: string;
  };
  colors: {
    primary: string; // HSL values
    primaryForeground: string;
    secondary: string;
    secondaryForeground: string;
    accent: string;
    accentForeground: string;
    background: string;
    foreground: string;
    muted: string;
    mutedForeground: string;
    card: string;
    cardForeground: string;
    border: string;
  };
  fonts: {
    heading: string;
    body: string;
  };
  heroStyle: 'split' | 'fullWidth' | 'cardOverImage' | 'minimal' | 'video' | 'gradient';
  contact: {
    phone: string;
    email: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    license: string;
    hours: string;
  };
  stats: {
    yearsInBusiness: number;
    projectsCompleted: number;
    googleRating: number;
    reviewCount: number;
  };
  hero: {
    headline: string;
    subheadline: string;
    ctaPrimary: string;
    ctaSecondary: string;
    image?: string;
  };
  trustBadges: TrustBadge[];
  services: ServiceItem[];
  serviceAreas: ServiceArea[];
  testimonials: Testimonial[];
  team: TeamMember[];
  about: {
    story: string;
    mission: string;
    values: string[];
    whyChooseUs: string[];
  };
  faq: FAQItem[];
  blogPosts: BlogPost[];
  process: {
    step: number;
    title: string;
    description: string;
    icon: string;
  }[];
}

// =========== TEMPLATE DEFINITIONS ===========

const generalContractor: ProTemplate = {
  id: 'general-contractor',
  brand: {
    name: 'BuildRight Construction',
    tagline: 'Quality Craftsmanship, On Time & On Budget',
  },
  colors: {
    primary: '25 95% 53%', // Orange
    primaryForeground: '0 0% 100%',
    secondary: '220 14% 20%',
    secondaryForeground: '0 0% 100%',
    accent: '45 93% 47%', // Gold
    accentForeground: '220 14% 20%',
    background: '0 0% 100%',
    foreground: '220 14% 10%',
    muted: '220 14% 96%',
    mutedForeground: '220 14% 45%',
    card: '0 0% 100%',
    cardForeground: '220 14% 10%',
    border: '220 14% 90%',
  },
  fonts: {
    heading: '"Plus Jakarta Sans", sans-serif',
    body: '"Inter", sans-serif',
  },
  heroStyle: 'split',
  contact: {
    phone: '(555) 234-5678',
    email: 'info@buildrightconstruction.com',
    address: '789 Builder Lane',
    city: 'Austin',
    state: 'TX',
    zip: '78701',
    license: 'TX GC #123456',
    hours: 'Mon-Fri: 7AM-6PM, Sat: 8AM-2PM',
  },
  stats: {
    yearsInBusiness: 18,
    projectsCompleted: 500,
    googleRating: 4.9,
    reviewCount: 312,
  },
  hero: {
    headline: 'Build Your Dream Home With Confidence',
    subheadline: 'Austin\'s most trusted general contractor. From foundations to finishing touches, we deliver exceptional craftsmanship on every project.',
    ctaPrimary: 'Get Free Estimate',
    ctaSecondary: 'Call Now',
  },
  trustBadges: [
    { icon: 'Shield', label: 'Licensed & Bonded' },
    { icon: 'Star', label: '5-Star Rated' },
    { icon: 'Award', label: 'BBB A+ Rating' },
  ],
  services: [
    { title: 'New Construction', description: 'Ground-up residential and commercial construction with expert project management.', icon: 'Building2', slug: 'new-construction', features: ['Custom floor plans', 'Permit handling', 'Full project management'] },
    { title: 'Home Additions', description: 'Seamless room additions, second stories, and garage conversions.', icon: 'Home', slug: 'additions', features: ['Architectural planning', 'Foundation work', 'Matching existing finishes'] },
    { title: 'Commercial Build-Outs', description: 'Office, retail, and restaurant build-outs to your exact specifications.', icon: 'Store', slug: 'commercial', features: ['TI improvements', 'ADA compliance', 'Fast turnaround'] },
    { title: 'Structural Repairs', description: 'Foundation repair, load-bearing wall modifications, and structural reinforcement.', icon: 'Hammer', slug: 'structural', features: ['Engineering reports', 'Pier installation', 'Beam replacement'] },
    { title: 'Outdoor Living', description: 'Decks, patios, pergolas, and outdoor kitchens built to last.', icon: 'Trees', slug: 'outdoor', features: ['Custom designs', 'Composite & wood', 'Built-in features'] },
    { title: 'Project Management', description: 'Let us manage your entire project from concept to completion.', icon: 'ClipboardList', slug: 'management', features: ['Budget tracking', 'Vendor coordination', 'Timeline management'] },
  ],
  serviceAreas: [
    { name: 'Austin', description: 'Our home base serving all of Austin proper with fast response times.' },
    { name: 'Round Rock', description: 'Full construction services throughout Round Rock and Brushy Creek.' },
    { name: 'Cedar Park', description: 'Serving Cedar Park homeowners with quality craftsmanship.' },
    { name: 'Pflugerville', description: 'Proud to serve the growing Pflugerville community.' },
    { name: 'Georgetown', description: 'Historic and modern construction in Georgetown.' },
    { name: 'Lakeway', description: 'Luxury home construction in the Lakeway area.' },
    { name: 'Dripping Springs', description: 'Custom homes and additions in Hill Country.' },
    { name: 'Bee Cave', description: 'Serving Bee Cave and surrounding communities.' },
    { name: 'Leander', description: 'Quality construction in fast-growing Leander.' },
    { name: 'Kyle', description: 'Residential and commercial projects in Kyle.' },
  ],
  testimonials: [
    { name: 'Michael R.', rating: 5, text: 'BuildRight completed our home addition flawlessly. They stayed on budget and actually finished early. The attention to detail was incredible.', location: 'Austin, TX', service: 'Home Addition' },
    { name: 'Jennifer L.', rating: 5, text: 'We hired them for a complete remodel and couldn\'t be happier. Professional crew, clean work site, and the results exceeded our expectations.', location: 'Round Rock, TX', service: 'Full Remodel' },
    { name: 'David & Sarah M.', rating: 5, text: 'From the first meeting to final walkthrough, BuildRight was exceptional. They built our dream home exactly as we envisioned.', location: 'Lakeway, TX', service: 'New Construction' },
  ],
  team: [
    { name: 'James Mitchell', role: 'Owner & Lead Contractor', bio: '25+ years in construction. Licensed general contractor with a passion for quality.' },
    { name: 'Maria Santos', role: 'Project Manager', bio: 'Keeps every project on track and on budget with meticulous attention to detail.' },
    { name: 'Robert Chen', role: 'Lead Carpenter', bio: 'Master carpenter with expertise in custom millwork and fine finishes.' },
    { name: 'Amanda Foster', role: 'Design Consultant', bio: 'Helps clients envision and plan their perfect spaces.' },
  ],
  about: {
    story: 'BuildRight Construction was founded in 2006 with a simple mission: deliver exceptional construction quality without the headaches. Starting as a two-man crew, we\'ve grown into Central Texas\'s most trusted general contractor through honest work, fair pricing, and unwavering commitment to our clients.',
    mission: 'To build lasting structures and lasting relationships through quality craftsmanship, transparent communication, and genuine care for every project we undertake.',
    values: ['Integrity in every interaction', 'Quality over shortcuts', 'Clear communication always', 'Respect for your home and time'],
    whyChooseUs: ['18+ years serving Central Texas', 'In-house crews - no subcontractor surprises', 'Detailed proposals with no hidden costs', 'Warranty on all workmanship', 'Licensed, bonded, and fully insured', 'Real-time project updates'],
  },
  faq: [
    { question: 'How long does a typical home addition take?', answer: 'Most home additions take 3-6 months depending on size and complexity. We provide a detailed timeline during your free consultation.' },
    { question: 'Do you handle permits?', answer: 'Yes! We handle all permitting, inspections, and code compliance so you don\'t have to worry about paperwork.' },
    { question: 'What\'s your payment structure?', answer: 'We typically work with a phased payment schedule tied to project milestones. This protects both parties and keeps the project moving smoothly.' },
    { question: 'Can you work with my architect?', answer: 'Absolutely. We collaborate with architects, designers, and engineers regularly. We can also recommend trusted partners if needed.' },
  ],
  blogPosts: [
    { id: '1', title: 'Planning Your Home Addition: A Complete Guide', excerpt: 'Everything you need to know before breaking ground on your home addition project.', category: 'how-to', date: '2024-01-15', readTime: '8 min' },
    { id: '2', title: 'How Much Does a Home Addition Cost in 2024?', excerpt: 'A comprehensive breakdown of costs for various types of home additions in Texas.', category: 'cost-guides', date: '2024-01-10', readTime: '6 min' },
    { id: '3', title: '5 Questions to Ask Before Hiring a Contractor', excerpt: 'Protect yourself and your investment by asking these crucial questions.', category: 'hiring-tips', date: '2024-01-05', readTime: '5 min' },
  ],
  process: [
    { step: 1, title: 'Free Consultation', description: 'We meet to discuss your vision, needs, and budget.', icon: 'MessageSquare' },
    { step: 2, title: 'Detailed Proposal', description: 'Receive a comprehensive scope and transparent pricing.', icon: 'FileText' },
    { step: 3, title: 'Build Phase', description: 'Our skilled crews bring your vision to life.', icon: 'Hammer' },
    { step: 4, title: 'Final Walkthrough', description: 'We ensure every detail meets your expectations.', icon: 'CheckCircle' },
  ],
};

const customHomeBuilder: ProTemplate = {
  id: 'custom-home-builder',
  brand: {
    name: 'Prestige Custom Homes',
    tagline: 'Where Vision Meets Craftsmanship',
  },
  colors: {
    primary: '220 70% 25%', // Deep navy
    primaryForeground: '0 0% 100%',
    secondary: '45 60% 45%', // Warm gold
    secondaryForeground: '220 70% 10%',
    accent: '45 60% 45%',
    accentForeground: '220 70% 10%',
    background: '40 20% 98%',
    foreground: '220 70% 10%',
    muted: '40 15% 93%',
    mutedForeground: '220 20% 40%',
    card: '0 0% 100%',
    cardForeground: '220 70% 10%',
    border: '40 15% 88%',
  },
  fonts: {
    heading: '"Playfair Display", serif',
    body: '"Lato", sans-serif',
  },
  heroStyle: 'fullWidth',
  contact: {
    phone: '(555) 876-5432',
    email: 'build@prestigecustomhomes.com',
    address: '100 Luxury Lane',
    city: 'Scottsdale',
    state: 'AZ',
    zip: '85251',
    license: 'AZ ROC #298765',
    hours: 'Mon-Fri: 9AM-5PM, Sat: By Appointment',
  },
  stats: {
    yearsInBusiness: 25,
    projectsCompleted: 200,
    googleRating: 5.0,
    reviewCount: 156,
  },
  hero: {
    headline: 'Luxury Homes Built for Life',
    subheadline: 'Award-winning custom home builder crafting architectural masterpieces in Scottsdale and Paradise Valley. Your vision, elevated.',
    ctaPrimary: 'Schedule a Consultation',
    ctaSecondary: 'View Portfolio',
  },
  trustBadges: [
    { icon: 'Trophy', label: 'Award-Winning Builder' },
    { icon: 'Home', label: '200+ Homes Built' },
    { icon: 'Medal', label: 'NAHB Member' },
  ],
  services: [
    { title: 'Custom Home Design-Build', description: 'Full-service custom home construction from concept to completion.', icon: 'PenTool', slug: 'custom-homes', features: ['Architectural design', 'Interior design', 'Full construction'] },
    { title: 'Luxury Renovations', description: 'Transform your existing home into the estate you deserve.', icon: 'Sparkles', slug: 'renovations', features: ['Whole-home remodels', 'Kitchen & bath', 'Outdoor living'] },
    { title: 'Estate Development', description: 'Multi-lot estate planning and development services.', icon: 'Mountain', slug: 'estates', features: ['Land acquisition', 'Site planning', 'Multi-structure builds'] },
    { title: 'Smart Home Integration', description: 'Cutting-edge technology seamlessly integrated into your home.', icon: 'Wifi', slug: 'smart-home', features: ['Home automation', 'Security systems', 'Energy management'] },
  ],
  serviceAreas: [
    { name: 'Scottsdale', description: 'Serving North Scottsdale and Old Town with luxury custom homes.' },
    { name: 'Paradise Valley', description: 'Premier builder in Arizona\'s most exclusive community.' },
    { name: 'Cave Creek', description: 'Desert contemporary homes in Cave Creek.' },
    { name: 'Fountain Hills', description: 'Custom homes with stunning mountain views.' },
    { name: 'Carefree', description: 'Luxury estates in the serene Carefree area.' },
    { name: 'Rio Verde', description: 'Golf course and desert view homes.' },
  ],
  testimonials: [
    { name: 'The Harrington Family', rating: 5, text: 'Prestige built our 8,000 sq ft estate and exceeded every expectation. The attention to detail and quality of materials is unmatched.', location: 'Paradise Valley, AZ', service: 'Custom Home' },
    { name: 'Dr. Robert Kim', rating: 5, text: 'From the initial design to move-in, the process was seamless. They truly understand luxury living and it shows in every detail.', location: 'Scottsdale, AZ', service: 'Custom Home' },
    { name: 'Elizabeth & Thomas W.', rating: 5, text: 'Our third home with Prestige. There is simply no other builder we would trust with our vision.', location: 'Cave Creek, AZ', service: 'Estate Home' },
  ],
  team: [
    { name: 'William Prestige III', role: 'Founder & CEO', bio: 'Third-generation builder with an eye for timeless design.' },
    { name: 'Catherine Chen', role: 'Director of Design', bio: 'Award-winning architect bringing global inspiration to the desert.' },
    { name: 'Marcus Johnson', role: 'Construction Director', bio: '30 years ensuring every home meets our exacting standards.' },
  ],
  about: {
    story: 'For 25 years, Prestige Custom Homes has been the builder of choice for Arizona\'s most discerning homeowners. Founded on the principle that a home should be a perfect reflection of its owner, we have crafted over 200 architectural masterpieces across the Valley.',
    mission: 'To create exceptional homes that inspire and endure for generations.',
    values: ['Uncompromising quality', 'Timeless design', 'White-glove service', 'Sustainability'],
    whyChooseUs: ['Award-winning architectural designs', 'Only premium materials and finishes', 'Dedicated project team for each client', 'Transparent build process', 'Industry-leading warranty program', 'Exclusive partnerships with luxury vendors'],
  },
  faq: [
    { question: 'What is your typical price per square foot?', answer: 'Our custom homes typically range from $450-$800+ per square foot depending on finishes and complexity. We provide detailed budgets during the design phase.' },
    { question: 'How long does it take to build a custom home?', answer: 'Most custom homes take 14-24 months from groundbreaking to completion. Complex estates may take longer.' },
    { question: 'Do you only build in gated communities?', answer: 'We build wherever our clients wish, from private lots to exclusive golf communities. We assist with land acquisition if needed.' },
  ],
  blogPosts: [
    { id: '1', title: 'Trends in Luxury Home Design for 2024', excerpt: 'Discover the architectural and interior trends defining modern luxury.', category: 'how-to', date: '2024-01-20', readTime: '10 min' },
    { id: '2', title: 'The True Cost of Building a Custom Home', excerpt: 'Understanding the investment in your forever home.', category: 'cost-guides', date: '2024-01-12', readTime: '12 min' },
    { id: '3', title: 'Selecting the Right Lot for Your Dream Home', excerpt: 'Key considerations when purchasing land for custom construction.', category: 'how-to', date: '2024-01-05', readTime: '7 min' },
  ],
  process: [
    { step: 1, title: 'Discovery', description: 'An in-depth exploration of your vision and lifestyle.', icon: 'Compass' },
    { step: 2, title: 'Design', description: 'Collaborative design process with our award-winning team.', icon: 'PenTool' },
    { step: 3, title: 'Construction', description: 'Meticulous construction with weekly progress updates.', icon: 'Hammer' },
    { step: 4, title: 'Delivery', description: 'White-glove handoff of your completed masterpiece.', icon: 'Key' },
  ],
};

const roofer: ProTemplate = {
  id: 'roofer',
  brand: {
    name: 'TopShield Roofing',
    tagline: 'Your Roof. Our Reputation.',
  },
  colors: {
    primary: '210 100% 35%', // Strong blue
    primaryForeground: '0 0% 100%',
    secondary: '15 90% 55%', // Warm orange
    secondaryForeground: '0 0% 100%',
    accent: '15 90% 55%',
    accentForeground: '0 0% 100%',
    background: '210 20% 98%',
    foreground: '210 50% 10%',
    muted: '210 15% 94%',
    mutedForeground: '210 20% 45%',
    card: '0 0% 100%',
    cardForeground: '210 50% 10%',
    border: '210 15% 88%',
  },
  fonts: {
    heading: '"Montserrat", sans-serif',
    body: '"Open Sans", sans-serif',
  },
  heroStyle: 'gradient',
  contact: {
    phone: '(555) 789-ROOF',
    email: 'info@topshieldroofing.com',
    address: '456 Shingle Street',
    city: 'Denver',
    state: 'CO',
    zip: '80202',
    license: 'CO Roofing #45678',
    hours: 'Mon-Sat: 7AM-7PM, Sun: Emergency Only',
  },
  stats: {
    yearsInBusiness: 15,
    projectsCompleted: 3500,
    googleRating: 4.8,
    reviewCount: 892,
  },
  hero: {
    headline: 'Roofing Done Right. Guaranteed.',
    subheadline: 'Denver\'s #1 rated roofing contractor. Storm damage? Aging roof? We\'ve got you covered with free inspections and financing options.',
    ctaPrimary: 'Free Roof Inspection',
    ctaSecondary: 'Call For Emergency',
  },
  trustBadges: [
    { icon: 'Shield', label: 'Lifetime Warranty' },
    { icon: 'CloudRain', label: 'Storm Damage Experts' },
    { icon: 'DollarSign', label: 'Financing Available' },
  ],
  services: [
    { title: 'Roof Replacement', description: 'Complete tear-off and installation with premium materials.', icon: 'Home', slug: 'replacement', features: ['Shingle, tile, metal', 'Lifetime warranties', 'Haul-away included'] },
    { title: 'Storm Damage Repair', description: 'Fast response and insurance claim assistance.', icon: 'CloudLightning', slug: 'storm-damage', features: ['Free inspection', 'Insurance assistance', 'Emergency tarping'] },
    { title: 'Roof Repair', description: 'Leaks, flashing, vents, and more fixed fast.', icon: 'Wrench', slug: 'repair', features: ['Same-day service', 'Warranty repairs', 'Affordable fixes'] },
    { title: 'Commercial Roofing', description: 'Flat roofs, TPO, EPDM, and metal for businesses.', icon: 'Building', slug: 'commercial', features: ['Low-slope systems', 'Maintenance plans', 'Minimal disruption'] },
    { title: 'Gutter Installation', description: 'Seamless gutters and leaf protection systems.', icon: 'Droplets', slug: 'gutters', features: ['Seamless aluminum', 'Gutter guards', 'Downspout extensions'] },
    { title: 'Roof Inspections', description: 'Detailed reports for buying, selling, or peace of mind.', icon: 'Search', slug: 'inspections', features: ['Drone imaging', 'Written report', 'Free for replacement quotes'] },
  ],
  serviceAreas: [
    { name: 'Denver', description: 'Serving all Denver neighborhoods with fast response times.' },
    { name: 'Aurora', description: 'Full roofing services throughout Aurora.' },
    { name: 'Lakewood', description: 'Trusted roofer for Lakewood homeowners.' },
    { name: 'Arvada', description: 'Quality roofing in Arvada and Olde Town.' },
    { name: 'Westminster', description: 'Serving Westminster and surrounding areas.' },
    { name: 'Thornton', description: 'Roofing experts in Thornton.' },
    { name: 'Centennial', description: 'South Denver metro roofing services.' },
    { name: 'Highlands Ranch', description: 'HOA-approved roofing in Highlands Ranch.' },
    { name: 'Littleton', description: 'Reliable roofing in Littleton.' },
    { name: 'Parker', description: 'Serving Parker and the I-25 corridor.' },
  ],
  testimonials: [
    { name: 'Amanda P.', rating: 5, text: 'After the hail storm, TopShield was here within hours. They handled everything with insurance and our new roof looks amazing!', location: 'Denver, CO', service: 'Storm Damage' },
    { name: 'George T.', rating: 5, text: 'Got 5 quotes and TopShield had the best price AND the best warranty. Crew was professional and cleaned up perfectly.', location: 'Aurora, CO', service: 'Roof Replacement' },
    { name: 'Linda M.', rating: 5, text: 'They fixed a leak other roofers couldn\'t find. Honest, skilled, and reasonably priced. Highly recommend!', location: 'Lakewood, CO', service: 'Roof Repair' },
  ],
  team: [
    { name: 'Steve Richardson', role: 'Owner & Lead Estimator', bio: 'Master roofer with certifications from GAF, Owens Corning, and CertainTeed.' },
    { name: 'Carlos Mendez', role: 'Production Manager', bio: 'Oversees all crews ensuring quality and safety on every job.' },
    { name: 'Jenny Park', role: 'Insurance Specialist', bio: 'Helps homeowners navigate insurance claims for maximum coverage.' },
  ],
  about: {
    story: 'TopShield Roofing was born in 2009 after a devastating hail storm left thousands of Denver homeowners scrambling for help. Founder Steve Richardson saw too many fly-by-night contractors taking advantage of the situation. He started TopShield with a commitment to honest assessments, quality work, and standing behind every roof we install.',
    mission: 'To protect Colorado families with roofing solutions that last, backed by service that cares.',
    values: ['Honest assessments always', 'Quality materials only', 'Clean, respectful crews', 'We stand behind our work'],
    whyChooseUs: ['Manufacturer-certified installers', 'Industry-leading warranties', 'Free drone roof inspections', 'Insurance claim specialists', 'Flexible financing options', 'Locally owned since 2009'],
  },
  faq: [
    { question: 'How do I know if I need a new roof?', answer: 'Signs include missing shingles, granules in gutters, leaks, and visible wear. We offer free inspections to assess your roof\'s condition.' },
    { question: 'Will you work with my insurance company?', answer: 'Absolutely! We have a dedicated insurance specialist who will document damage and work directly with your adjuster.' },
    { question: 'What roofing materials do you recommend?', answer: 'It depends on your budget, style, and goals. We\'re certified installers for GAF, Owens Corning, and CertainTeed and can explain the pros and cons of each.' },
  ],
  blogPosts: [
    { id: '1', title: 'Colorado Hail Damage: What Every Homeowner Should Know', excerpt: 'How to spot hail damage and protect your investment.', category: 'maintenance', date: '2024-01-18', readTime: '6 min' },
    { id: '2', title: 'Asphalt vs Metal Roofing: Which is Right for You?', excerpt: 'Comparing costs, lifespans, and aesthetics.', category: 'cost-guides', date: '2024-01-10', readTime: '8 min' },
    { id: '3', title: 'How to File a Roofing Insurance Claim', excerpt: 'Step-by-step guide to getting your roof covered.', category: 'how-to', date: '2024-01-05', readTime: '7 min' },
  ],
  process: [
    { step: 1, title: 'Free Inspection', description: 'We thoroughly inspect your roof and document all findings.', icon: 'Search' },
    { step: 2, title: 'Detailed Estimate', description: 'Receive a transparent, itemized proposal.', icon: 'FileText' },
    { step: 3, title: 'Professional Install', description: 'Our certified crews complete the work.', icon: 'HardHat' },
    { step: 4, title: 'Final Walkthrough', description: 'We inspect with you and activate your warranty.', icon: 'CheckCircle' },
  ],
};

const plumber: ProTemplate = {
  id: 'plumber',
  brand: {
    name: 'FlowMaster Plumbing',
    tagline: '24/7 Plumbing You Can Trust',
  },
  colors: {
    primary: '199 89% 40%', // Bright blue
    primaryForeground: '0 0% 100%',
    secondary: '150 60% 40%', // Teal
    secondaryForeground: '0 0% 100%',
    accent: '45 100% 50%', // Yellow for emergency
    accentForeground: '199 89% 15%',
    background: '0 0% 100%',
    foreground: '199 50% 10%',
    muted: '199 20% 95%',
    mutedForeground: '199 20% 40%',
    card: '0 0% 100%',
    cardForeground: '199 50% 10%',
    border: '199 20% 88%',
  },
  fonts: {
    heading: '"Poppins", sans-serif',
    body: '"Roboto", sans-serif',
  },
  heroStyle: 'split',
  contact: {
    phone: '(555) 555-FLOW',
    email: 'service@flowmasterplumbing.com',
    address: '321 Pipe Way',
    city: 'Atlanta',
    state: 'GA',
    zip: '30301',
    license: 'GA Master Plumber #MP12345',
    hours: '24/7 Emergency Service Available',
  },
  stats: {
    yearsInBusiness: 12,
    projectsCompleted: 8000,
    googleRating: 4.9,
    reviewCount: 1245,
  },
  hero: {
    headline: 'Plumbing Problems? We\'re On Our Way.',
    subheadline: 'Atlanta\'s fastest response plumbing team. From clogged drains to burst pipes, we fix it right the first time. Available 24/7.',
    ctaPrimary: 'Call Now',
    ctaSecondary: 'Schedule Service',
  },
  trustBadges: [
    { icon: 'Clock', label: '24/7 Emergency Service' },
    { icon: 'BadgeCheck', label: 'Upfront Pricing' },
    { icon: 'Shield', label: 'Licensed & Insured' },
  ],
  services: [
    { title: 'Emergency Repairs', description: 'Burst pipes, major leaks, and backups handled fast.', icon: 'Siren', slug: 'emergency', features: ['Immediate dispatch', 'No overtime charges', 'Damage mitigation'] },
    { title: 'Drain Cleaning', description: 'Clogged sinks, toilets, showers, and main lines cleared.', icon: 'Droplets', slug: 'drains', features: ['Video inspection', 'Hydro jetting', 'Root removal'] },
    { title: 'Water Heater Service', description: 'Repair, replacement, and tankless installation.', icon: 'Thermometer', slug: 'water-heaters', features: ['Same-day install', 'Tankless experts', 'Efficiency upgrades'] },
    { title: 'Leak Detection', description: 'Find hidden leaks before they cause damage.', icon: 'Search', slug: 'leak-detection', features: ['Non-invasive', 'Thermal imaging', 'Slab leak specialists'] },
    { title: 'Pipe Repair & Replacement', description: 'Repiping, trenchless repairs, and pipe relining.', icon: 'Wrench', slug: 'pipes', features: ['Trenchless options', 'PEX upgrades', 'Camera inspection'] },
    { title: 'Fixture Installation', description: 'Faucets, toilets, showers, and more installed right.', icon: 'Bath', slug: 'fixtures', features: ['All brands', 'Clean installation', 'Disposal of old fixtures'] },
    { title: 'Gas Line Service', description: 'Gas line installation, repair, and safety checks.', icon: 'Flame', slug: 'gas-lines', features: ['Licensed gas work', 'Leak detection', 'Appliance hookup'] },
    { title: 'Sewer Services', description: 'Sewer line repair, replacement, and cleaning.', icon: 'Construction', slug: 'sewer', features: ['Camera inspection', 'Trenchless repair', 'Backup prevention'] },
  ],
  serviceAreas: [
    { name: 'Atlanta', description: 'All Atlanta neighborhoods with rapid response times.' },
    { name: 'Marietta', description: 'Full plumbing services in Marietta.' },
    { name: 'Decatur', description: 'Serving Decatur homes and businesses.' },
    { name: 'Alpharetta', description: 'North Atlanta\'s trusted plumber.' },
    { name: 'Roswell', description: 'Fast service in Roswell and Sandy Springs.' },
    { name: 'Duluth', description: 'Gwinnett County plumbing experts.' },
    { name: 'Smyrna', description: 'Reliable plumbing in Smyrna.' },
    { name: 'Kennesaw', description: 'Serving Kennesaw and Acworth.' },
    { name: 'Johns Creek', description: 'Premium plumbing in Johns Creek.' },
    { name: 'Lawrenceville', description: 'Gwinnett\'s go-to plumber.' },
    { name: 'Brookhaven', description: 'Fast response in Brookhaven.' },
    { name: 'Dunwoody', description: 'Plumbing experts in Dunwoody.' },
  ],
  testimonials: [
    { name: 'Karen S.', rating: 5, text: 'Water was gushing from under my house at 2 AM. FlowMaster was here in 30 minutes and had it fixed by sunrise. Lifesavers!', location: 'Atlanta, GA', service: 'Emergency Repair' },
    { name: 'Marcus D.', rating: 5, text: 'They explained everything, showed me options, and didn\'t pressure me. Fair price and excellent work on our water heater.', location: 'Marietta, GA', service: 'Water Heater' },
    { name: 'Paula R.', rating: 5, text: 'Finally, a plumber who shows up on time, keeps things clean, and charges what they quoted. Will use again!', location: 'Decatur, GA', service: 'Drain Cleaning' },
  ],
  team: [
    { name: 'Damon Wallace', role: 'Owner & Master Plumber', bio: '20 years solving Atlanta\'s plumbing problems.' },
    { name: 'Tyrone Jackson', role: 'Lead Service Technician', bio: 'Expert in leak detection and water heaters.' },
    { name: 'Lisa Chang', role: 'Customer Care Manager', bio: 'Ensures every customer has a great experience.' },
  ],
  about: {
    story: 'FlowMaster Plumbing was founded in 2012 by Damon Wallace, a second-generation plumber who learned the trade from his father. Frustrated with plumbing companies that overcharge and underdeliver, Damon built FlowMaster on a simple promise: show up fast, fix it right, and charge fairly.',
    mission: 'To provide reliable, honest plumbing services that Atlanta families can count on, day or night.',
    values: ['Rapid response', 'Upfront pricing', 'Respectful service', 'First-time fixes'],
    whyChooseUs: ['Average 45-minute response time', 'Price quoted before work begins', 'Background-checked technicians', 'Clean, uniformed professionals', '100% satisfaction guarantee', 'No overtime or holiday upcharges'],
  },
  faq: [
    { question: 'Do you charge extra for weekends or holidays?', answer: 'No! We never charge overtime. The price quoted is the price you pay, regardless of when you call.' },
    { question: 'How fast can you get here?', answer: 'For emergencies, we typically arrive within 45 minutes. For scheduled appointments, we offer 2-hour windows.' },
    { question: 'Do you offer warranties on repairs?', answer: 'Yes, all repairs come with at least a 1-year warranty. Many services include extended warranties.' },
  ],
  blogPosts: [
    { id: '1', title: 'What to Do When a Pipe Bursts', excerpt: 'Quick steps to minimize damage before we arrive.', category: 'how-to', date: '2024-01-15', readTime: '4 min' },
    { id: '2', title: 'Tank vs Tankless Water Heaters', excerpt: 'Which option is right for your home and budget?', category: 'cost-guides', date: '2024-01-08', readTime: '6 min' },
    { id: '3', title: 'Signs You Have a Hidden Water Leak', excerpt: 'Catch leaks early before they cause major damage.', category: 'maintenance', date: '2024-01-02', readTime: '5 min' },
  ],
  process: [
    { step: 1, title: 'Call or Book', description: 'Reach us 24/7 by phone or online booking.', icon: 'Phone' },
    { step: 2, title: 'Fast Dispatch', description: 'A technician is on the way, often within an hour.', icon: 'Truck' },
    { step: 3, title: 'Upfront Quote', description: 'We diagnose and quote before any work begins.', icon: 'FileText' },
    { step: 4, title: 'Fixed Right', description: 'Problem solved, mess cleaned, warranty provided.', icon: 'CheckCircle' },
  ],
};

const electrician: ProTemplate = {
  id: 'electrician',
  brand: {
    name: 'PowerPro Electric',
    tagline: 'Safe. Reliable. Always On.',
  },
  colors: {
    primary: '45 100% 50%', // Electric yellow
    primaryForeground: '0 0% 10%',
    secondary: '220 90% 20%', // Dark blue
    secondaryForeground: '0 0% 100%',
    accent: '45 100% 50%',
    accentForeground: '0 0% 10%',
    background: '220 30% 98%',
    foreground: '220 90% 10%',
    muted: '220 20% 94%',
    mutedForeground: '220 20% 40%',
    card: '0 0% 100%',
    cardForeground: '220 90% 10%',
    border: '220 15% 88%',
  },
  fonts: {
    heading: '"Archivo", sans-serif',
    body: '"Source Sans Pro", sans-serif',
  },
  heroStyle: 'cardOverImage',
  contact: {
    phone: '(555) 321-WATT',
    email: 'info@powerproelectric.com',
    address: '567 Circuit Drive',
    city: 'Seattle',
    state: 'WA',
    zip: '98101',
    license: 'WA Electrical #EL-12345',
    hours: 'Mon-Fri: 7AM-6PM, Sat: 8AM-4PM, 24/7 Emergency',
  },
  stats: {
    yearsInBusiness: 22,
    projectsCompleted: 12000,
    googleRating: 4.9,
    reviewCount: 678,
  },
  hero: {
    headline: 'Electrical Experts You Can Trust',
    subheadline: 'Seattle\'s top-rated electricians. From panel upgrades to EV charger installation, we handle it all safely and to code.',
    ctaPrimary: 'Get Free Estimate',
    ctaSecondary: 'Emergency Service',
  },
  trustBadges: [
    { icon: 'Zap', label: 'Licensed Electricians' },
    { icon: 'ShieldCheck', label: 'Code Compliant' },
    { icon: 'Clock', label: '24/7 Emergency' },
  ],
  services: [
    { title: 'Panel Upgrades', description: 'Upgrade your electrical panel for modern power needs.', icon: 'Gauge', slug: 'panels', features: ['100-400 amp upgrades', 'Subpanel installation', 'Load calculations'] },
    { title: 'EV Charger Installation', description: 'Level 2 home charging stations installed right.', icon: 'BatteryCharging', slug: 'ev-chargers', features: ['All brands', 'Permit handling', 'Utility rebate assistance'] },
    { title: 'Electrical Repairs', description: 'Outlets, switches, circuits, and troubleshooting.', icon: 'Wrench', slug: 'repairs', features: ['Same-day service', 'Outlet replacement', 'Circuit tracing'] },
    { title: 'Lighting Installation', description: 'Recessed, landscape, smart, and decorative lighting.', icon: 'Lightbulb', slug: 'lighting', features: ['LED upgrades', 'Dimmer installation', 'Outdoor lighting'] },
    { title: 'Whole-Home Rewiring', description: 'Safe, modern wiring for older homes.', icon: 'Home', slug: 'rewiring', features: ['Knob & tube replacement', 'Aluminum wiring fixes', 'Minimal wall damage'] },
    { title: 'Generator Installation', description: 'Standby generators for uninterrupted power.', icon: 'Power', slug: 'generators', features: ['Generac certified', 'Transfer switches', 'Maintenance plans'] },
    { title: 'Smart Home Wiring', description: 'Network, audio, and automation wiring.', icon: 'Wifi', slug: 'smart-home', features: ['Structured wiring', 'Smart switches', 'USB outlets'] },
  ],
  serviceAreas: [
    { name: 'Seattle', description: 'All Seattle neighborhoods including Downtown, Capitol Hill, and Ballard.' },
    { name: 'Bellevue', description: 'Full electrical services on the Eastside.' },
    { name: 'Redmond', description: 'Serving Redmond homes and tech campuses.' },
    { name: 'Kirkland', description: 'Reliable electrical work in Kirkland.' },
    { name: 'Renton', description: 'South King County electrical experts.' },
    { name: 'Issaquah', description: 'Serving Issaquah and Sammamish.' },
    { name: 'Tacoma', description: 'Expanding to serve Tacoma and Pierce County.' },
    { name: 'Everett', description: 'North Sound electrical services.' },
  ],
  testimonials: [
    { name: 'Chris B.', rating: 5, text: 'PowerPro installed our Tesla charger perfectly. They handled the permit, upgraded our panel, and had it running in two days.', location: 'Seattle, WA', service: 'EV Charger' },
    { name: 'Margaret W.', rating: 5, text: 'Our 1920s home needed rewiring. They did it with minimal disruption and left our walls looking untouched. Incredible work!', location: 'Bellevue, WA', service: 'Rewiring' },
    { name: 'Jason T.', rating: 5, text: 'Called at 10 PM when we lost power to half the house. Electrician arrived in an hour and solved it quickly. True professionals.', location: 'Kirkland, WA', service: 'Emergency' },
  ],
  team: [
    { name: 'Ryan Mitchell', role: 'Owner & Master Electrician', bio: '25 years in the trade with specialization in residential and light commercial.' },
    { name: 'Sarah Kim', role: 'Lead Technician', bio: 'Expert in smart home integration and EV charging systems.' },
    { name: 'Mike Torres', role: 'Commercial Division Lead', bio: 'Handles larger projects and tenant improvements.' },
  ],
  about: {
    story: 'PowerPro Electric was founded in 2002 by Ryan Mitchell after years of seeing electricians cut corners. Our mission from day one has been simple: do the job right, safely, and to code—every single time. Today we\'re Seattle\'s most trusted electrical contractor.',
    mission: 'To provide safe, reliable electrical services that protect your home and power your life.',
    values: ['Safety first, always', 'Code compliance guaranteed', 'Clean, professional service', 'Standing behind our work'],
    whyChooseUs: ['Master electricians on every job', 'All work inspected and to code', 'We explain what we\'re doing and why', 'No mess left behind', 'Flat-rate pricing—no surprises', 'Lifetime warranty on workmanship'],
  },
  faq: [
    { question: 'Do I need a permit for electrical work?', answer: 'Most electrical work requires a permit. We handle all permitting and inspections as part of our service.' },
    { question: 'How do I know if I need a panel upgrade?', answer: 'If your panel is over 25 years old, has fuses instead of breakers, or you\'re adding major appliances, you likely need an upgrade. We offer free assessments.' },
    { question: 'Can you install any brand of EV charger?', answer: 'Yes, we install all major brands including Tesla, ChargePoint, Wallbox, and more. We\'ll help you choose the best option for your vehicle and home.' },
  ],
  blogPosts: [
    { id: '1', title: 'Is Your Home\'s Wiring Safe?', excerpt: 'Warning signs of outdated or dangerous electrical systems.', category: 'maintenance', date: '2024-01-20', readTime: '5 min' },
    { id: '2', title: 'EV Charger Installation: What to Expect', excerpt: 'Everything you need to know before going electric at home.', category: 'how-to', date: '2024-01-12', readTime: '7 min' },
    { id: '3', title: 'How Much Does a Panel Upgrade Cost?', excerpt: 'Breaking down the costs and factors involved.', category: 'cost-guides', date: '2024-01-05', readTime: '6 min' },
  ],
  process: [
    { step: 1, title: 'Free Estimate', description: 'We assess your needs and provide upfront pricing.', icon: 'Calculator' },
    { step: 2, title: 'Schedule', description: 'Pick a time that works for you.', icon: 'Calendar' },
    { step: 3, title: 'Expert Work', description: 'Our licensed electricians complete the job to code.', icon: 'Zap' },
    { step: 4, title: 'Inspection & Warranty', description: 'We ensure everything is perfect and back our work.', icon: 'ShieldCheck' },
  ],
};

const hvac: ProTemplate = {
  id: 'hvac',
  brand: {
    name: 'ClimateCare HVAC',
    tagline: 'Your Comfort. Our Priority.',
  },
  colors: {
    primary: '199 89% 40%', // Cooling blue
    primaryForeground: '0 0% 100%',
    secondary: '15 80% 50%', // Warming orange
    secondaryForeground: '0 0% 100%',
    accent: '150 60% 45%', // Fresh green
    accentForeground: '0 0% 100%',
    background: '199 15% 98%',
    foreground: '199 50% 10%',
    muted: '199 15% 94%',
    mutedForeground: '199 20% 40%',
    card: '0 0% 100%',
    cardForeground: '199 50% 10%',
    border: '199 15% 88%',
  },
  fonts: {
    heading: '"Plus Jakarta Sans", sans-serif',
    body: '"Inter", sans-serif',
  },
  heroStyle: 'split',
  contact: {
    phone: '(555) 555-HVAC',
    email: 'service@climatecarehvac.com',
    address: '1234 Comfort Lane',
    city: 'Phoenix',
    state: 'AZ',
    zip: '85001',
    license: 'AZ HVAC #CR-12345',
    hours: '24/7 Emergency Service',
  },
  stats: {
    yearsInBusiness: 20,
    projectsCompleted: 15000,
    googleRating: 4.9,
    reviewCount: 2150,
  },
  hero: {
    headline: 'Cool Summers. Warm Winters. Always Comfortable.',
    subheadline: 'Phoenix\'s most trusted HVAC contractor. Fast repairs, expert installations, and maintenance plans that prevent breakdowns.',
    ctaPrimary: 'Schedule Service',
    ctaSecondary: '24/7 Emergency',
  },
  trustBadges: [
    { icon: 'ThermometerSun', label: '24/7 Emergency Service' },
    { icon: 'Award', label: 'NATE Certified' },
    { icon: 'DollarSign', label: 'Financing Available' },
  ],
  services: [
    { title: 'AC Repair', description: 'Fast, reliable air conditioning repair to beat the heat.', icon: 'Snowflake', slug: 'ac-repair', features: ['Same-day service', 'All brands', 'Flat-rate pricing'] },
    { title: 'AC Installation', description: 'High-efficiency systems installed right.', icon: 'Wind', slug: 'ac-installation', features: ['Energy Star systems', 'Proper sizing', 'Warranty included'] },
    { title: 'Heating Repair', description: 'Keep your home warm when temperatures drop.', icon: 'Flame', slug: 'heating-repair', features: ['Furnace & heat pump', 'Fast response', 'Safety checks'] },
    { title: 'Heating Installation', description: 'Modern, efficient heating systems.', icon: 'ThermometerSun', slug: 'heating-installation', features: ['Heat pumps', 'Furnaces', 'Zoning systems'] },
    { title: 'Maintenance Plans', description: 'Prevent breakdowns and extend equipment life.', icon: 'Wrench', slug: 'maintenance', features: ['Bi-annual tune-ups', 'Priority scheduling', 'Discounts on repairs'] },
    { title: 'Indoor Air Quality', description: 'Breathe cleaner, healthier air at home.', icon: 'Leaf', slug: 'air-quality', features: ['Air purifiers', 'Humidifiers', 'Duct cleaning'] },
    { title: 'Ductwork', description: 'Efficient duct design, repair, and sealing.', icon: 'ArrowUpDown', slug: 'ductwork', features: ['Leak sealing', 'Insulation', 'New installation'] },
    { title: 'Commercial HVAC', description: 'Climate control for businesses.', icon: 'Building2', slug: 'commercial', features: ['RTUs', 'Split systems', 'Maintenance contracts'] },
  ],
  serviceAreas: [
    { name: 'Phoenix', description: 'Serving all of Phoenix with fast response times.' },
    { name: 'Scottsdale', description: 'Premier HVAC service in Scottsdale.' },
    { name: 'Tempe', description: 'ASU area and all of Tempe.' },
    { name: 'Mesa', description: 'East Valley\'s trusted HVAC contractor.' },
    { name: 'Chandler', description: 'Quality service in Chandler.' },
    { name: 'Gilbert', description: 'Growing with Gilbert families.' },
    { name: 'Glendale', description: 'West Valley HVAC experts.' },
    { name: 'Peoria', description: 'Serving Peoria and Sun City.' },
    { name: 'Surprise', description: 'Northwest Valley service area.' },
    { name: 'Goodyear', description: 'Southwest Valley coverage.' },
  ],
  testimonials: [
    { name: 'Patricia M.', rating: 5, text: 'AC died in July at 115 degrees. ClimateCare had someone here in 2 hours and it was fixed by dinner. Amazing service!', location: 'Phoenix, AZ', service: 'AC Repair' },
    { name: 'Robert H.', rating: 5, text: 'They installed a new high-efficiency system that cut our summer electric bill by 35%. Pays for itself!', location: 'Scottsdale, AZ', service: 'AC Installation' },
    { name: 'Sandra L.', rating: 5, text: 'We\'ve been on their maintenance plan for 5 years. Never had a breakdown. That\'s worth everything in Phoenix summers.', location: 'Tempe, AZ', service: 'Maintenance' },
  ],
  team: [
    { name: 'Tony Alvarez', role: 'Owner & NATE-Certified Technician', bio: '25 years keeping Phoenix cool.' },
    { name: 'Jessica Wright', role: 'Service Manager', bio: 'Ensures every customer is a happy customer.' },
    { name: 'Carlos Reyes', role: 'Lead Installation Tech', bio: 'Expert in high-efficiency system design.' },
  ],
  about: {
    story: 'ClimateCare HVAC was founded in 2004 with one goal: to be the HVAC company that actually shows up when you need them. In Phoenix\'s extreme heat, reliable AC isn\'t a luxury—it\'s a necessity. That\'s why we\'re available 24/7, 365 days a year.',
    mission: 'To keep every Phoenix home comfortable, no matter the weather.',
    values: ['Reliability above all', 'Fair, upfront pricing', 'Technical excellence', 'Treating every home like our own'],
    whyChooseUs: ['NATE-certified technicians', '24/7 emergency service', 'Same-day appointments available', 'Financing with approved credit', 'All work guaranteed', 'Locally owned since 2004'],
  },
  faq: [
    { question: 'How often should I service my AC in Phoenix?', answer: 'We recommend twice yearly: once in spring before summer, and once in fall. In our extreme climate, regular maintenance is essential.' },
    { question: 'What\'s the average AC lifespan in Arizona?', answer: 'Due to our intense summers, AC units typically last 10-15 years in Arizona versus 15-20 elsewhere. Regular maintenance extends lifespan.' },
    { question: 'Do you offer financing?', answer: 'Yes! We offer flexible financing options with approved credit, including 0% APR for qualified buyers.' },
  ],
  blogPosts: [
    { id: '1', title: 'Surviving Phoenix Summers: AC Tips', excerpt: 'How to keep your AC running and bills low in extreme heat.', category: 'maintenance', date: '2024-01-18', readTime: '5 min' },
    { id: '2', title: 'Heat Pump vs Traditional AC in Arizona', excerpt: 'Is a heat pump right for the desert climate?', category: 'cost-guides', date: '2024-01-10', readTime: '7 min' },
    { id: '3', title: 'When to Repair vs Replace Your AC', excerpt: 'Making the smart financial decision for your home.', category: 'cost-guides', date: '2024-01-05', readTime: '6 min' },
  ],
  process: [
    { step: 1, title: 'Call or Book Online', description: 'Reach us 24/7 for service or schedule ahead.', icon: 'Phone' },
    { step: 2, title: 'Fast Arrival', description: 'A technician arrives within your time window.', icon: 'Truck' },
    { step: 3, title: 'Honest Assessment', description: 'We diagnose and explain your options clearly.', icon: 'Search' },
    { step: 4, title: 'Comfort Restored', description: 'Your system is fixed or installed, guaranteed.', icon: 'ThermometerSun' },
  ],
};

const remodeler: ProTemplate = {
  id: 'remodeler',
  brand: {
    name: 'Transform Home Remodeling',
    tagline: 'Kitchens. Baths. Whole-Home Renovations.',
  },
  colors: {
    primary: '160 60% 35%', // Sage green
    primaryForeground: '0 0% 100%',
    secondary: '30 40% 45%', // Warm taupe
    secondaryForeground: '0 0% 100%',
    accent: '45 70% 55%', // Warm gold
    accentForeground: '160 60% 10%',
    background: '40 30% 98%',
    foreground: '160 30% 10%',
    muted: '40 20% 93%',
    mutedForeground: '160 15% 40%',
    card: '0 0% 100%',
    cardForeground: '160 30% 10%',
    border: '40 15% 88%',
  },
  fonts: {
    heading: '"Cormorant Garamond", serif',
    body: '"Nunito Sans", sans-serif',
  },
  heroStyle: 'fullWidth',
  contact: {
    phone: '(555) 234-HOME',
    email: 'design@transformremodeling.com',
    address: '890 Design District',
    city: 'San Diego',
    state: 'CA',
    zip: '92101',
    license: 'CSLB #987654',
    hours: 'Mon-Fri: 9AM-6PM, Sat: 10AM-4PM',
  },
  stats: {
    yearsInBusiness: 16,
    projectsCompleted: 850,
    googleRating: 4.9,
    reviewCount: 423,
  },
  hero: {
    headline: 'Love the Home You\'re In',
    subheadline: 'San Diego\'s award-winning design-build firm. From stunning kitchen makeovers to complete home transformations, we bring your vision to life.',
    ctaPrimary: 'Schedule Design Consult',
    ctaSecondary: 'View Portfolio',
  },
  trustBadges: [
    { icon: 'Award', label: 'Best of Houzz' },
    { icon: 'Palette', label: 'In-House Design' },
    { icon: 'DollarSign', label: 'Financing Available' },
  ],
  services: [
    { title: 'Kitchen Remodeling', description: 'Custom kitchens designed for how you live.', icon: 'ChefHat', slug: 'kitchens', features: ['Custom cabinetry', 'Layout optimization', 'High-end finishes'] },
    { title: 'Bathroom Remodeling', description: 'Spa-like bathrooms that start your day right.', icon: 'Bath', slug: 'bathrooms', features: ['Walk-in showers', 'Soaking tubs', 'Custom vanities'] },
    { title: 'Whole-Home Renovation', description: 'Transform your entire home, room by room.', icon: 'Home', slug: 'whole-home', features: ['Design coordination', 'Phase planning', 'Living-in-place options'] },
    { title: 'ADU & Additions', description: 'Add space and value with ADUs and room additions.', icon: 'Building', slug: 'additions', features: ['ADU specialists', 'Second stories', 'Garage conversions'] },
    { title: 'Outdoor Living', description: 'Extend your living space outdoors.', icon: 'Trees', slug: 'outdoor', features: ['Outdoor kitchens', 'Pergolas', 'Fire features'] },
    { title: 'Aging in Place', description: 'Modifications for safety and accessibility.', icon: 'Heart', slug: 'aging-in-place', features: ['Walk-in tubs', 'Grab bars', 'Wider doorways'] },
  ],
  serviceAreas: [
    { name: 'San Diego', description: 'All San Diego neighborhoods from La Jolla to South Bay.' },
    { name: 'La Jolla', description: 'Luxury remodeling in La Jolla.' },
    { name: 'Del Mar', description: 'Coastal home renovations in Del Mar.' },
    { name: 'Encinitas', description: 'Serving Encinitas and Leucadia.' },
    { name: 'Carlsbad', description: 'North County remodeling experts.' },
    { name: 'Coronado', description: 'Island-style renovations in Coronado.' },
    { name: 'Point Loma', description: 'Waterfront home specialists.' },
    { name: 'Mission Hills', description: 'Historic home renovations.' },
  ],
  testimonials: [
    { name: 'The Patterson Family', rating: 5, text: 'Transform took our 1970s kitchen and made it magazine-worthy. The design process was collaborative and the craftsmanship is impeccable.', location: 'La Jolla, CA', service: 'Kitchen Remodel' },
    { name: 'Nancy B.', rating: 5, text: 'Our master bath is now a spa. They thought of details we never would have—heated floors, a rain shower, perfect lighting. Worth every penny.', location: 'Del Mar, CA', service: 'Bathroom Remodel' },
    { name: 'Mark & Lisa D.', rating: 5, text: 'We lived in the house during a whole-home renovation and they made it as painless as possible. Professional, clean, and the results are stunning.', location: 'San Diego, CA', service: 'Whole-Home' },
  ],
  team: [
    { name: 'Jennifer Marks', role: 'Founder & Lead Designer', bio: 'Award-winning designer with 20+ years transforming San Diego homes.' },
    { name: 'David Chen', role: 'Construction Director', bio: 'Ensures every project is built to the highest standards.' },
    { name: 'Elena Rodriguez', role: 'Interior Designer', bio: 'Brings warmth and personality to every space.' },
    { name: 'Tom Williams', role: 'Project Manager', bio: 'Keeps projects on track and clients informed.' },
  ],
  about: {
    story: 'Transform Home Remodeling began in 2008 when designer Jennifer Marks saw too many beautiful designs poorly executed. She built a design-build firm where design vision and construction excellence work hand-in-hand, creating spaces that are as functional as they are beautiful.',
    mission: 'To create thoughtfully designed, expertly crafted spaces that enhance how our clients live.',
    values: ['Design with purpose', 'Craftsmanship without compromise', 'Clear, constant communication', 'Respect for your home and time'],
    whyChooseUs: ['In-house design team', 'Own crews—no subcontractor chaos', 'Fixed pricing—no change order surprises', 'Portfolio of completed projects to tour', 'Financing options available', 'Houzz Best of Service 6 years running'],
  },
  faq: [
    { question: 'How long does a kitchen remodel take?', answer: 'Most kitchen remodels take 8-12 weeks of construction, plus 4-6 weeks of design and ordering. We provide a detailed timeline before starting.' },
    { question: 'Can I stay in my home during renovation?', answer: 'Usually, yes! We set up dust barriers, work in phases when possible, and maintain access to essentials. We\'ll discuss the specifics for your project.' },
    { question: 'Do you handle design or do I need a designer?', answer: 'We\'re a full design-build firm with in-house designers. From concept to completion, we handle everything.' },
  ],
  blogPosts: [
    { id: '1', title: 'Kitchen Design Trends That Actually Last', excerpt: 'Beyond the trends—designing a kitchen you\'ll love for decades.', category: 'how-to', date: '2024-01-18', readTime: '8 min' },
    { id: '2', title: 'What Does a Kitchen Remodel Really Cost?', excerpt: 'Honest breakdown of kitchen remodeling costs in San Diego.', category: 'cost-guides', date: '2024-01-12', readTime: '10 min' },
    { id: '3', title: 'ADU 101: Everything You Need to Know', excerpt: 'Your guide to adding an ADU in California.', category: 'how-to', date: '2024-01-05', readTime: '12 min' },
  ],
  process: [
    { step: 1, title: 'Discovery Call', description: 'We learn about your vision, needs, and budget.', icon: 'MessageSquare' },
    { step: 2, title: 'Design Phase', description: 'Our designers create detailed plans and selections.', icon: 'PenTool' },
    { step: 3, title: 'Build Phase', description: 'Our craftsmen bring the design to life.', icon: 'Hammer' },
    { step: 4, title: 'Reveal', description: 'The moment you see your transformed space.', icon: 'Sparkles' },
  ],
};

const painter: ProTemplate = {
  id: 'painter',
  brand: {
    name: 'ColorCraft Painting',
    tagline: 'Professional Painting. Flawless Finishes.',
  },
  colors: {
    primary: '260 50% 50%', // Purple
    primaryForeground: '0 0% 100%',
    secondary: '180 50% 40%', // Teal
    secondaryForeground: '0 0% 100%',
    accent: '45 90% 55%', // Yellow
    accentForeground: '260 50% 15%',
    background: '0 0% 100%',
    foreground: '260 40% 10%',
    muted: '260 20% 95%',
    mutedForeground: '260 15% 40%',
    card: '0 0% 100%',
    cardForeground: '260 40% 10%',
    border: '260 15% 90%',
  },
  fonts: {
    heading: '"DM Sans", sans-serif',
    body: '"Work Sans", sans-serif',
  },
  heroStyle: 'gradient',
  contact: {
    phone: '(555) 456-PAINT',
    email: 'hello@colorcraftpainting.com',
    address: '456 Brush Boulevard',
    city: 'Portland',
    state: 'OR',
    zip: '97201',
    license: 'OR CCB #198765',
    hours: 'Mon-Sat: 7AM-6PM',
  },
  stats: {
    yearsInBusiness: 14,
    projectsCompleted: 4200,
    googleRating: 4.9,
    reviewCount: 567,
  },
  hero: {
    headline: 'Transform Your Space With Color',
    subheadline: 'Portland\'s premier painting contractor. Interior, exterior, and cabinet refinishing with meticulous prep and premium paints.',
    ctaPrimary: 'Get Free Estimate',
    ctaSecondary: 'View Color Gallery',
  },
  trustBadges: [
    { icon: 'Paintbrush', label: 'Premium Paints Only' },
    { icon: 'Shield', label: '5-Year Warranty' },
    { icon: 'Leaf', label: 'Low-VOC Options' },
  ],
  services: [
    { title: 'Interior Painting', description: 'Walls, ceilings, trim, and accent features.', icon: 'Home', slug: 'interior', features: ['Color consultation', 'Furniture protection', 'Clean edges'] },
    { title: 'Exterior Painting', description: 'Siding, trim, doors, and decks.', icon: 'Building', slug: 'exterior', features: ['Power washing', 'Proper prep', 'Weather-resistant'] },
    { title: 'Cabinet Refinishing', description: 'Transform cabinets without replacement.', icon: 'Square', slug: 'cabinets', features: ['Spray finish', 'Hardware update', 'Fraction of replacement cost'] },
    { title: 'Commercial Painting', description: 'Offices, retail, and multi-family.', icon: 'Building2', slug: 'commercial', features: ['After-hours work', 'Minimal disruption', 'Fast turnaround'] },
    { title: 'Deck & Fence Staining', description: 'Protect and beautify outdoor wood.', icon: 'Trees', slug: 'staining', features: ['Proper prep', 'Premium stains', 'Even coverage'] },
    { title: 'Specialty Finishes', description: 'Accent walls, textures, and faux finishes.', icon: 'Sparkles', slug: 'specialty', features: ['Accent walls', 'Limewash', 'Venetian plaster'] },
  ],
  serviceAreas: [
    { name: 'Portland', description: 'All Portland neighborhoods from NW to SE.' },
    { name: 'Lake Oswego', description: 'Premium painting in Lake Oswego.' },
    { name: 'West Linn', description: 'Serving West Linn and Oregon City.' },
    { name: 'Tigard', description: 'Painting services in Tigard and Tualatin.' },
    { name: 'Beaverton', description: 'Full coverage in Beaverton.' },
    { name: 'Hillsboro', description: 'Serving Hillsboro and surrounding areas.' },
    { name: 'Vancouver', description: 'Expanding to Clark County, WA.' },
    { name: 'Milwaukie', description: 'SE metro painting services.' },
  ],
  testimonials: [
    { name: 'Rachel G.', rating: 5, text: 'ColorCraft painted our entire interior and it looks flawless. Their prep work is incredible—you can tell they care about quality.', location: 'Portland, OR', service: 'Interior' },
    { name: 'Brian & Michelle P.', rating: 5, text: 'They refinished our kitchen cabinets and it looks like we got a whole new kitchen. Saved us $20k compared to replacing them!', location: 'Lake Oswego, OR', service: 'Cabinets' },
    { name: 'Daniel S.', rating: 5, text: 'Exterior was peeling badly. They scraped, primed, and painted. House looks brand new and the warranty gives us peace of mind.', location: 'West Linn, OR', service: 'Exterior' },
  ],
  team: [
    { name: 'Chris Martinez', role: 'Owner & Lead Estimator', bio: '20 years in the painting industry with an eye for perfect finishes.' },
    { name: 'Amy Foster', role: 'Color Consultant', bio: 'Helps clients find the perfect palette for their space.' },
    { name: 'Jake Thompson', role: 'Production Manager', bio: 'Ensures every crew delivers ColorCraft quality.' },
  ],
  about: {
    story: 'ColorCraft Painting was founded in 2010 by Chris Martinez, a painter who was tired of seeing shortcuts in the industry. We believe that great painting is 80% prep and 100% pride in the finished product. That\'s why we\'ve become Portland\'s most recommended painting company.',
    mission: 'To deliver beautiful, lasting paint jobs through meticulous preparation and premium materials.',
    values: ['Prep work matters', 'Premium paints only', 'Respect for your home', 'Standing behind our work'],
    whyChooseUs: ['Detailed prep before any painting', 'Premium Sherwin-Williams and Benjamin Moore paints', 'Color consultation included', 'Furniture moved and protected', '5-year warranty on workmanship', 'Low-VOC and eco-friendly options'],
  },
  faq: [
    { question: 'What paint brands do you use?', answer: 'We use premium paints from Sherwin-Williams and Benjamin Moore. These cost more but last longer and look better.' },
    { question: 'Do you offer color consultations?', answer: 'Yes! Our color consultant will help you choose the perfect colors for your space at no extra charge with any painting project.' },
    { question: 'How long does interior painting take?', answer: 'A typical 3-bedroom home takes 3-5 days depending on scope. We\'ll provide a specific timeline in your estimate.' },
  ],
  blogPosts: [
    { id: '1', title: 'Choosing the Right Paint Finish', excerpt: 'Matte vs eggshell vs semi-gloss—which is right for each room?', category: 'how-to', date: '2024-01-15', readTime: '5 min' },
    { id: '2', title: 'How Much Does It Cost to Paint a House?', excerpt: 'Interior and exterior painting costs in the Portland area.', category: 'cost-guides', date: '2024-01-10', readTime: '7 min' },
    { id: '3', title: 'Prep Work: Why It Makes or Breaks Your Paint Job', excerpt: 'The difference between a paint job that lasts 3 years vs 10.', category: 'how-to', date: '2024-01-05', readTime: '6 min' },
  ],
  process: [
    { step: 1, title: 'Free Estimate', description: 'We measure, assess conditions, and provide a detailed quote.', icon: 'Calculator' },
    { step: 2, title: 'Color Selection', description: 'Our consultant helps you finalize your palette.', icon: 'Palette' },
    { step: 3, title: 'Prep & Paint', description: 'Meticulous prep followed by expert application.', icon: 'Paintbrush' },
    { step: 4, title: 'Final Inspection', description: 'We walk through together and ensure perfection.', icon: 'CheckCircle' },
  ],
};

const landscaper: ProTemplate = {
  id: 'landscaper',
  brand: {
    name: 'GreenScape Outdoor Living',
    tagline: 'Design. Build. Maintain.',
  },
  colors: {
    primary: '142 60% 35%', // Forest green
    primaryForeground: '0 0% 100%',
    secondary: '35 80% 45%', // Warm brown
    secondaryForeground: '0 0% 100%',
    accent: '80 60% 50%', // Lime
    accentForeground: '142 60% 10%',
    background: '80 20% 98%',
    foreground: '142 40% 10%',
    muted: '80 15% 93%',
    mutedForeground: '142 20% 40%',
    card: '0 0% 100%',
    cardForeground: '142 40% 10%',
    border: '80 15% 88%',
  },
  fonts: {
    heading: '"Libre Baskerville", serif',
    body: '"Source Sans Pro", sans-serif',
  },
  heroStyle: 'fullWidth',
  contact: {
    phone: '(555) 567-LAWN',
    email: 'design@greenscapeoutdoor.com',
    address: '789 Garden Way',
    city: 'Nashville',
    state: 'TN',
    zip: '37201',
    license: 'TN Landscape #LC-45678',
    hours: 'Mon-Sat: 7AM-5PM',
  },
  stats: {
    yearsInBusiness: 18,
    projectsCompleted: 1800,
    googleRating: 4.8,
    reviewCount: 634,
  },
  hero: {
    headline: 'Your Outdoor Paradise Awaits',
    subheadline: 'Nashville\'s award-winning landscape design and build firm. From patios to plantings, we create outdoor spaces you\'ll love.',
    ctaPrimary: 'Free Design Consult',
    ctaSecondary: 'View Portfolio',
  },
  trustBadges: [
    { icon: 'Trees', label: 'Design-Build Firm' },
    { icon: 'Award', label: 'Award Winning' },
    { icon: 'Leaf', label: 'Sustainable Practices' },
  ],
  services: [
    { title: 'Landscape Design', description: 'Custom designs that transform your property.', icon: 'PenTool', slug: 'design', features: ['3D renderings', 'Plant selection', 'Phased planning'] },
    { title: 'Hardscape Installation', description: 'Patios, walkways, retaining walls, and driveways.', icon: 'Square', slug: 'hardscape', features: ['Pavers & stone', 'Retaining walls', 'Outdoor kitchens'] },
    { title: 'Planting & Softscape', description: 'Trees, shrubs, perennials, and ornamental gardens.', icon: 'Flower2', slug: 'planting', features: ['Native plants', 'Seasonal color', 'Pollinator gardens'] },
    { title: 'Outdoor Lighting', description: 'Illuminate your landscape for safety and beauty.', icon: 'Lightbulb', slug: 'lighting', features: ['Path lighting', 'Accent lighting', 'Smart controls'] },
    { title: 'Water Features', description: 'Fountains, ponds, and bubbling rocks.', icon: 'Waves', slug: 'water-features', features: ['Fountains', 'Pondless features', 'Maintenance included'] },
    { title: 'Lawn Care & Maintenance', description: 'Keep your landscape looking its best.', icon: 'Scissors', slug: 'maintenance', features: ['Mowing programs', 'Fertilization', 'Seasonal cleanup'] },
    { title: 'Irrigation Systems', description: 'Efficient watering that saves water and money.', icon: 'Droplets', slug: 'irrigation', features: ['Smart controllers', 'Drip systems', 'Repairs & upgrades'] },
    { title: 'Fire Features', description: 'Fire pits and fireplaces for outdoor gatherings.', icon: 'Flame', slug: 'fire-features', features: ['Gas fire pits', 'Wood-burning', 'Custom builds'] },
  ],
  serviceAreas: [
    { name: 'Nashville', description: 'All Nashville neighborhoods and Davidson County.' },
    { name: 'Franklin', description: 'Premier landscaping in historic Franklin.' },
    { name: 'Brentwood', description: 'Serving Brentwood\'s finest properties.' },
    { name: 'Murfreesboro', description: 'Rutherford County landscape services.' },
    { name: 'Hendersonville', description: 'Lakeside and Sumner County properties.' },
    { name: 'Mt. Juliet', description: 'Growing with Mt. Juliet.' },
    { name: 'Nolensville', description: 'Williamson County\'s growing community.' },
    { name: 'Spring Hill', description: 'Southern suburbs service area.' },
  ],
  testimonials: [
    { name: 'The Anderson Family', rating: 5, text: 'GreenScape transformed our backyard into an oasis. The patio, fire pit, and plantings are exactly what we envisioned. We live outside now!', location: 'Franklin, TN', service: 'Full Landscape' },
    { name: 'Jeff M.', rating: 5, text: 'Professional from start to finish. They designed a low-maintenance landscape that looks incredible year-round. Couldn\'t be happier.', location: 'Brentwood, TN', service: 'Landscape Design' },
    { name: 'Catherine L.', rating: 5, text: 'We\'ve used them for maintenance for 3 years. Always reliable, always thorough. Our lawn has never looked better.', location: 'Nashville, TN', service: 'Maintenance' },
  ],
  team: [
    { name: 'Michael Green', role: 'Founder & Lead Designer', bio: 'Landscape architect with 25 years creating outdoor living spaces.' },
    { name: 'Sarah Mitchell', role: 'Design Director', bio: 'Brings fresh creativity to every project.' },
    { name: 'Roberto Castillo', role: 'Construction Manager', bio: 'Ensures every installation meets our high standards.' },
  ],
  about: {
    story: 'GreenScape Outdoor Living was founded in 2006 by landscape architect Michael Green with a vision to bring resort-quality outdoor spaces to Nashville homes. Today, we\'re an award-winning design-build firm with a reputation for creativity, quality, and making clients\' outdoor dreams reality.',
    mission: 'To design and build outdoor living spaces that enhance lives and connect people with nature.',
    values: ['Design with intention', 'Build to last', 'Sustainable practices', 'Client collaboration'],
    whyChooseUs: ['Licensed landscape architect on staff', 'Own crews—quality controlled', '3D design renderings', 'Native and sustainable plant focus', 'Warranty on all installations', 'Maintenance plans to protect your investment'],
  },
  faq: [
    { question: 'How much does a landscape project cost?', answer: 'Projects range widely based on scope. Simple plantings start around $5k, while full outdoor living spaces with patios and features can be $50k+. We design to your budget.' },
    { question: 'What\'s the best time to start a landscape project?', answer: 'Fall and spring are ideal for planting. Hardscape can be done year-round except during extreme weather. Design can start anytime!' },
    { question: 'Do you maintain the landscapes you install?', answer: 'Yes! We offer maintenance programs to protect your investment and keep your landscape looking its best.' },
  ],
  blogPosts: [
    { id: '1', title: 'Low-Maintenance Landscaping Ideas', excerpt: 'Beautiful landscapes that don\'t require constant upkeep.', category: 'how-to', date: '2024-01-18', readTime: '7 min' },
    { id: '2', title: 'Patio Materials Compared', excerpt: 'Pavers, flagstone, concrete—which is right for your project?', category: 'cost-guides', date: '2024-01-12', readTime: '8 min' },
    { id: '3', title: 'Native Plants for Tennessee Gardens', excerpt: 'Beautiful, sustainable plant choices for Middle Tennessee.', category: 'how-to', date: '2024-01-05', readTime: '6 min' },
  ],
  process: [
    { step: 1, title: 'Consultation', description: 'We visit your property and discuss your vision.', icon: 'MessageSquare' },
    { step: 2, title: 'Design', description: 'Our team creates a custom design with 3D visuals.', icon: 'PenTool' },
    { step: 3, title: 'Installation', description: 'Our crews bring the design to life.', icon: 'Hammer' },
    { step: 4, title: 'Enjoy', description: 'Your new outdoor paradise is ready.', icon: 'Smile' },
  ],
};

const flooring: ProTemplate = {
  id: 'flooring',
  brand: {
    name: 'FloorPro Specialists',
    tagline: 'Flooring Done Right. Every Time.',
  },
  colors: {
    primary: '25 60% 40%', // Warm wood brown
    primaryForeground: '0 0% 100%',
    secondary: '200 30% 30%', // Slate blue
    secondaryForeground: '0 0% 100%',
    accent: '45 90% 55%', // Gold
    accentForeground: '25 60% 15%',
    background: '35 30% 98%',
    foreground: '25 40% 10%',
    muted: '35 20% 93%',
    mutedForeground: '25 15% 40%',
    card: '0 0% 100%',
    cardForeground: '25 40% 10%',
    border: '35 15% 88%',
  },
  fonts: {
    heading: '"Josefin Sans", sans-serif',
    body: '"Karla", sans-serif',
  },
  heroStyle: 'split',
  contact: {
    phone: '(555) 876-FLOOR',
    email: 'info@floorprospecialists.com',
    address: '123 Hardwood Lane',
    city: 'Minneapolis',
    state: 'MN',
    zip: '55401',
    license: 'MN BC #654321',
    hours: 'Mon-Fri: 8AM-6PM, Sat: 9AM-4PM',
  },
  stats: {
    yearsInBusiness: 20,
    projectsCompleted: 5500,
    googleRating: 4.9,
    reviewCount: 892,
  },
  hero: {
    headline: 'Beautiful Floors. Expert Installation.',
    subheadline: 'Minneapolis\'s trusted flooring experts. Hardwood, LVP, tile, and carpet installed with precision and backed by our quality guarantee.',
    ctaPrimary: 'Free In-Home Estimate',
    ctaSecondary: 'Visit Showroom',
  },
  trustBadges: [
    { icon: 'Award', label: 'Certified Installers' },
    { icon: 'Shield', label: 'Lifetime Warranty' },
    { icon: 'Star', label: '5-Star Rated' },
  ],
  services: [
    { title: 'Hardwood Flooring', description: 'Solid and engineered hardwood installation.', icon: 'Trees', slug: 'hardwood', features: ['All species', 'Custom staining', 'Dustless sanding'] },
    { title: 'LVP/LVT Flooring', description: 'Luxury vinyl plank and tile for durability.', icon: 'Layers', slug: 'lvp', features: ['Waterproof options', 'Pet-friendly', 'Fast installation'] },
    { title: 'Tile & Stone', description: 'Ceramic, porcelain, and natural stone.', icon: 'Square', slug: 'tile', features: ['Floor & wall', 'Heated floors', 'Shower/bath tile'] },
    { title: 'Carpet Installation', description: 'Quality carpet for comfort and style.', icon: 'Home', slug: 'carpet', features: ['Major brands', 'Stain-resistant', 'Pad included'] },
    { title: 'Hardwood Refinishing', description: 'Restore your existing hardwood floors.', icon: 'Sparkles', slug: 'refinishing', features: ['Dustless sanding', 'Custom stains', 'New finishes'] },
    { title: 'Floor Repair', description: 'Fix squeaks, damage, and wear.', icon: 'Wrench', slug: 'repair', features: ['Board replacement', 'Subfloor repair', 'Transition work'] },
  ],
  serviceAreas: [
    { name: 'Minneapolis', description: 'All Minneapolis neighborhoods.' },
    { name: 'St. Paul', description: 'Full service in St. Paul.' },
    { name: 'Edina', description: 'Premium flooring in Edina.' },
    { name: 'Bloomington', description: 'South metro flooring experts.' },
    { name: 'Plymouth', description: 'Serving Plymouth and Wayzata.' },
    { name: 'Eden Prairie', description: 'SW metro coverage.' },
    { name: 'Maple Grove', description: 'North metro flooring.' },
    { name: 'Woodbury', description: 'East metro service area.' },
    { name: 'Minnetonka', description: 'Lake area specialists.' },
    { name: 'Eagan', description: 'Dakota County coverage.' },
  ],
  testimonials: [
    { name: 'Kevin & Julie H.', rating: 5, text: 'FloorPro installed gorgeous white oak throughout our main floor. The attention to detail was incredible—every board aligned perfectly.', location: 'Edina, MN', service: 'Hardwood' },
    { name: 'Stephanie R.', rating: 5, text: 'They refinished our 100-year-old hardwoods and they look brand new. The dustless process meant our house stayed clean!', location: 'Minneapolis, MN', service: 'Refinishing' },
    { name: 'Tom B.', rating: 5, text: 'LVP in the basement was done in one day. Looks amazing and we don\'t have to worry about moisture. Great recommendation and install.', location: 'St. Paul, MN', service: 'LVP' },
  ],
  team: [
    { name: 'Erik Johansson', role: 'Owner & Master Installer', bio: '25 years installing floors in the Twin Cities.' },
    { name: 'Lisa Thompson', role: 'Design Consultant', bio: 'Helps clients find the perfect flooring for their lifestyle.' },
    { name: 'Carlos Ruiz', role: 'Installation Manager', bio: 'Leads our team of certified installers.' },
  ],
  about: {
    story: 'FloorPro Specialists began in 2004 when Erik Johansson, a third-generation floor installer, decided to start a company focused on doing things right. No shortcuts, no subcontractors, just skilled craftsmen who take pride in every floor we install.',
    mission: 'To provide beautiful, durable flooring installations backed by unmatched craftsmanship and service.',
    values: ['Craftsmanship first', 'Honest recommendations', 'Clean, professional work', 'Standing behind every floor'],
    whyChooseUs: ['Our own trained installers—never subcontractors', 'Full showroom to see and touch samples', 'Dustless refinishing technology', 'Furniture moving included', 'Lifetime warranty on installation', 'Financing available'],
  },
  faq: [
    { question: 'How long does hardwood installation take?', answer: 'Most hardwood installations take 2-5 days depending on area size and complexity. Refinishing typically adds 3-4 days for curing.' },
    { question: 'Can you install over existing flooring?', answer: 'Often, yes. LVP and some engineered hardwoods can go over existing floors if in good condition. We\'ll assess during your free estimate.' },
    { question: 'What flooring is best for pets?', answer: 'LVP is excellent for pets—waterproof and scratch-resistant. Harder hardwoods like oak also perform well. We\'ll help you choose based on your household.' },
  ],
  blogPosts: [
    { id: '1', title: 'Hardwood vs LVP: Which is Right for You?', excerpt: 'Comparing cost, durability, and aesthetics.', category: 'cost-guides', date: '2024-01-15', readTime: '8 min' },
    { id: '2', title: 'Caring for Your Hardwood Floors', excerpt: 'Tips to keep your hardwoods beautiful for decades.', category: 'maintenance', date: '2024-01-10', readTime: '5 min' },
    { id: '3', title: 'Flooring Trends for 2024', excerpt: 'What\'s popular and what actually lasts.', category: 'how-to', date: '2024-01-05', readTime: '6 min' },
  ],
  process: [
    { step: 1, title: 'Free Estimate', description: 'We measure and assess your space at no cost.', icon: 'Ruler' },
    { step: 2, title: 'Selection', description: 'Visit our showroom or browse samples at home.', icon: 'Palette' },
    { step: 3, title: 'Installation', description: 'Our craftsmen install your new floors.', icon: 'Hammer' },
    { step: 4, title: 'Enjoy', description: 'Walk on beautiful floors backed by our warranty.', icon: 'Smile' },
  ],
};

// Export all templates
export const templates: Record<ProTemplateId, ProTemplate> = {
  'general-contractor': generalContractor,
  'custom-home-builder': customHomeBuilder,
  'roofer': roofer,
  'plumber': plumber,
  'electrician': electrician,
  'hvac': hvac,
  'remodeler': remodeler,
  'painter': painter,
  'landscaper': landscaper,
  'flooring': flooring,
};

export const templateList: { id: ProTemplateId; name: string; icon: string }[] = [
  { id: 'general-contractor', name: 'General Contractor', icon: 'HardHat' },
  { id: 'custom-home-builder', name: 'Custom Home Builder', icon: 'Home' },
  { id: 'roofer', name: 'Roofer', icon: 'Home' },
  { id: 'plumber', name: 'Plumber', icon: 'Droplets' },
  { id: 'electrician', name: 'Electrician', icon: 'Zap' },
  { id: 'hvac', name: 'HVAC', icon: 'ThermometerSun' },
  { id: 'remodeler', name: 'Kitchen & Bath', icon: 'ChefHat' },
  { id: 'painter', name: 'Painter', icon: 'Paintbrush' },
  { id: 'landscaper', name: 'Landscaper', icon: 'Trees' },
  { id: 'flooring', name: 'Flooring', icon: 'Layers' },
];

export const defaultTemplateId: ProTemplateId = 'general-contractor';
